import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { renderToStaticMarkup } from "@hot-loader/react-dom/server";
import parse from "html-react-parser";
import Moment from "moment";
import sanitizeHtml from "sanitize-html";
import {
  Button,
  Card,
  Checkbox,
  Container,
  Dimmer,
  Header,
  Icon,
  Popup,
} from "semantic-ui-react";

import { DeleteComment, EditComment, HideComment } from "data/libs/comments";
import publishUpdate from "data/messages/actions";

import { SetAlert } from "components/Alerts/actions";
import { SetConfirmationModal } from "components/ConfirmationModal/actions";
import { withRedtailUrl } from "components/Crm/hooks/useRedtailUrl";
import Expandable from "components/Expandable";
import { Loading } from "components/Loading";
import { PrivacyModeContext } from "components/PrivacyMode/Context";
import { RichTextEditor } from "components/RichTextEditor";
import {
  RemoveCommentTaskId,
  SetTaskComplete,
  SetTaskIncomplete,
} from "components/Task/actions";

import FormModal from "scenes/Hubly/components/Workspace/components/FormModal/FormModal";

import Autosaver from "./CommentDraft/components/Autosaver";
import DeleteDraftMenuButton from "./CommentDraft/DeleteDraftMenuButton";
import { renderMarkup } from "../utils/parsing";
import DeleteCommentAssociationButton from "./DeleteCommentAssociation";
import LinkedEntity from "./LinkedEntity";

class CardComment extends React.Component {
  static contextType = PrivacyModeContext;

  static defaultProps = {
    task: {},
    clientWorkflow: {},
    disabled: false,
    onEditClientComment: () => {},
    onDeleteClientComment: () => {},
  };

  static propTypes = {
    advisor: PropTypes.shape({
      id: PropTypes.string.isRequired,
      integrations: PropTypes.array.isRequired,
    }).isRequired,
    hub: PropTypes.object.isRequired,
    externalId: PropTypes.string.isRequired,
    comment: PropTypes.object.isRequired,
    onEditClientComment: PropTypes.func,
    onDeleteClientComment: PropTypes.func,
    client: PropTypes.object.isRequired,
    clientWorkflow: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    removeCommentTaskId: PropTypes.func.isRequired,
    onRemoveCommentAssociationId: PropTypes.func.isRequired,
    setConfirmationModal: PropTypes.func.isRequired,
    setTaskComplete: PropTypes.func.isRequired,
    setTaskIncomplete: PropTypes.func.isRequired,
    task: PropTypes.shape({
      id: PropTypes.string,
      completed: PropTypes.bool,
      title: PropTypes.string,
      completedAt: PropTypes.string,
      formInstanceId: PropTypes.string,
      workflowName: PropTypes.string,
      formInstance: PropTypes.object,
    }),
    setAlert: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    redtailUrl: PropTypes.string.isRequired,
  };

  state = {
    body: "",
    editing: false,
    loading: false,
    menuOpen: false,
    openFormModal: false,
    draftGid:
      this.props.comment.draft?.id ||
      (this.props.comment?.commentType === "DRAFT"
        ? this.props.comment.id
        : ""),
  };

  modules = {
    clipboard: {
      matchVisual: false,
    },
  };

  toggleCompletion = () => {
    const { task, clientWorkflow, setTaskComplete, setTaskIncomplete } =
      this.props;
    if (!task.completed) {
      if (task.formInstanceId && !task.formInstance?.isFilled) {
        this.setState({ openFormModal: true });
      } else {
        setTaskComplete(clientWorkflow, task.id);
      }
    } else {
      setTaskIncomplete(clientWorkflow, task.id);
    }
  };

  toggleTask = () => {
    const { task, setConfirmationModal } = this.props;

    if (!task.completed) {
      this.toggleCompletion();
      return;
    }

    const markTaskIncomplete = () => {
      this.toggleCompletion();
    };

    const params = {
      title: "Uncheck Task",
      message: `This task was marked completed on
        ${Moment(task.completedAt).format("MMM Do, YYYY [at] h:mm a")}.
        Are you sure you want to remove this timestamp?`,
      icon: "square outline",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Confirm",
          callBack: markTaskIncomplete,
          color: "red",
        },
      ],
    };

    setConfirmationModal(params);
    this.setState({ menuOpen: false });
  };

  handleBodyHTML = (content) => {
    this.setState({ body: content });
  };

  reloadCommentAssociationInfo = (comment) => {
    const { onRemoveCommentAssociationId } = this.props;
    if (comment.taskUuid || comment.clientWorkflowUuid) {
      onRemoveCommentAssociationId(comment);
    }
  };

  deleteComment = () => {
    const { comment, setAlert, onDeleteClientComment, setConfirmationModal } =
      this.props;
    const remove = () => {
      this.setState({ loading: true });
      DeleteComment(comment.uuid)
        .then(async () => {
          if (onDeleteClientComment) await onDeleteClientComment(comment);
          return this.reloadCommentAssociationInfo(comment);
        })
        .then(() => {
          setAlert({ type: "success", text: "Comment deleted." });
        })
        .catch((error) => {
          console.warn(error);
          setAlert({ type: "error", text: "Failed to delete comment." });
        })
        .finally(() => {
          this.setState({ loading: false });
        });
    };
    const cancel = () => {
      this.setState({ menuOpen: false, loading: false });
    };
    const params = {
      title: "Delete Comment",
      message: "Do you want to delete the comment?  This cannot be undone.",
      icon: "delete",
      buttons: [
        {
          text: "Cancel",
          callBack: cancel,
        },
        {
          text: "Delete Comment",
          callBack: remove,
          color: "red",
        },
      ],
    };
    setConfirmationModal(params);
    this.setState({ menuOpen: false });
  };

  hideEditor = () => {
    this.setState((state) => {
      return {
        editing: !state.editing,
        body: "",
        menuOpen: false,
      };
    });
  };

  editComment = () => {
    const { advisor, externalId, comment, hub } = this.props;

    const isRedtailIntegrated =
      advisor.integrations.find((integration) => {
        return integration.hubId === hub.id && integration.type === "redtail";
      }) !== undefined;

    if (isRedtailIntegrated && comment.commentType !== "DRAFT") {
      const url = `${this.props.redtailUrl}/contacts/${externalId}`;
      window.open(url, "_blank");
    } else {
      this.setState((state) => {
        return {
          editing: !state.editing,
          body: comment.draft ? comment.draft.content : comment.content,
        };
      });
    }
  };

  hideComment = () => {
    const { comment, setAlert, onEditClientComment } = this.props;
    const request = {
      isHidden: true,
    };

    this.setState({ menuOpen: false });

    HideComment(comment.uuid, request)
      .then(async (response) => {
        if (onEditClientComment) await onEditClientComment(response);
        return this.reloadCommentAssociationInfo(comment);
      })
      .then(() => {
        setAlert({ type: "success", text: "Comment hidden." });
      })
      .catch((error) => {
        console.warn(error);
        setAlert({ type: "error", text: "Failed to hide comment." });
      });
  };

  updateComment = (publishAction) => {
    const { body } = this.state; // comment content
    const { comment, task, onEditClientComment, setAlert } = this.props;
    this.setState({ loading: true });

    // to be replaced by renderMarkup
    const parsedBody = parse(body, {
      replace: (domNode) => {
        // Add // to links without http or https at start
        if (domNode.attribs && domNode.attribs.href) {
          let url = domNode.attribs.href;
          if (
            !domNode.attribs.href.startsWith("http://") &&
            !domNode.attribs.href.startsWith("https://") &&
            !domNode.attribs.href.startsWith("//") &&
            !domNode.attribs.href.startsWith("mailto:")
          ) {
            url = `//${domNode.attribs.href}`;
          }
          return React.createElement(
            "a",
            { href: url, target: "_blank" },
            domNode.children.length > 0 ? domNode.children[0].data : url
          );
        }
        return domNode;
      },
    });
    const request = {
      content: renderToStaticMarkup(parsedBody),
      taskId: task.id,
    };
    let performEditComment = () => {
      return EditComment(comment.uuid, request);
    };
    // if we have an optional new mutation provided - use it instead
    if (publishAction) {
      const { advisor, hub } = this.props;
      const newCommentOnPublish = comment.commentType === "DRAFT";
      performEditComment = () => {
        return publishAction(
          renderMarkup(body, advisor, hub, newCommentOnPublish)
        );
      };
    }

    return performEditComment()
      .then(async (response) => {
        this.hideEditor();
        // Temporarily set the new Date to now() - this will get updated to the proper date on next comment sync
        response.externalUpdatedAt = new Date();
        return onEditClientComment(response);
      })
      .catch((error) => {
        console.warn(error);
        setAlert({ type: "error", text: "Failed to edit comment." });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  toggleMenu = () => {
    this.setState((state) => {
      return { menuOpen: !state.menuOpen };
    });
  };

  handleCommentAssociationRemoval = (removeAction, notify = false) => {
    const { comment, removeCommentTaskId, task, setAlert } = this.props;
    this.setState({ loading: true });
    removeAction()
      .then(() => {
        if (notify) {
          setAlert({
            type: "success",
            text: `Successfully removed association from comment`,
          });
        }
        removeCommentTaskId(comment, task);
      })
      .catch((error) => {
        if (notify) {
          setAlert({
            type: "error",
            text: `Failed to remove task from comment.`,
          });
        }
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  editingMarkup = () => {
    const { piiMask } = this.context;
    const { client, clientWorkflow, comment, onEditClientComment, task } =
      this.props;
    const { body, loading, draftGid } = this.state;
    // nbsp breaks quill, need to replace before parsing
    // Also need to convert Wealthbox emojis to another object, as they don't work as is
    let parsedBody = parse(body.replace("&nbsp;", " "), {
      replace: (domNode) => {
        // Parsing Wealthbox emojis to convert to a span with the alt unicode
        if (
          domNode.attribs &&
          domNode.attribs.class === "js-emoji editor-emoji fr-fic fr-dii"
        ) {
          return React.createElement("span", {}, domNode.attribs.alt);
        }
        return domNode;
      },
    });
    // Render to static markup convert <br> to <br/>, which Quill removes.  Need to convert back to <br>.
    parsedBody = renderToStaticMarkup(parsedBody).replace(/<br\/>/g, "<br>");
    // Need to decode all html entitles, as they break Quill
    // The next 3 lines are a trick to get decode all html entities
    const txt = document.createElement("textarea");
    txt.innerHTML = parsedBody;
    parsedBody = txt.value;

    const linkedEntityTitle = task.title || clientWorkflow.name;
    const newCommentOnPublish = comment.commentType === "DRAFT";
    return (
      <React.Fragment>
        <div style={{ marginTop: "1em" }} id={task.id}>
          <Header as="h5" style={{ marginBottom: "0.25em" }}>
            Edit Comment
          </Header>
          <RichTextEditor
            className={piiMask("fs-block dd-privacy-mask")}
            data-test="update-comment-input"
            defaultValue={parsedBody}
            onChange={(content) => {
              this.handleBodyHTML(content);
            }}
            style={{ marginBottom: "0.5em" }}
            readOnly={loading}
          />
          {linkedEntityTitle && (
            <LinkedEntity
              title={linkedEntityTitle}
              entityType={comment.relatedEntityType}
            >
              <DeleteCommentAssociationButton
                disabled={loading}
                onLegacyDelete={() => {
                  return this.handleCommentAssociationRemoval(() => {
                    return EditComment(comment.uuid, { taskId: null });
                  }, true);
                }}
                onDelete={this.handleCommentAssociationRemoval}
                comment={comment}
              />
            </LinkedEntity>
          )}
          <Autosaver
            comment={comment}
            content={parsedBody}
            commentsLoading={loading}
            onPublish={this.updateComment}
            onClose={() => {
              this.hideEditor();
              return onEditClientComment();
            }}
            draftGid={draftGid}
            setDraftGid={(newDraftGid) => {
              this.setState({ draftGid: newDraftGid });
            }}
            taskUuid={task.id}
            setLoading={(status = true) => {
              this.setState({ loading: status });
            }}
            createNewComment={newCommentOnPublish}
            clientId={client.id}
          />
        </div>
      </React.Fragment>
    );
  };

  displayMarkup = () => {
    const { piiMask } = this.context;
    const { loading, menuOpen, openFormModal } = this.state;
    const {
      advisor,
      comment,
      hub,
      task,
      setTaskComplete,
      clientWorkflow,
      disabled,
      onEditClientComment,
      removeCommentTaskId,
    } = this.props;
    const textStyle = task.completed
      ? {
          textDecorationLine: "line-through",
          textDecorationStyle: "solid",
          lineHeight: "1.14285714em",
          marginTop: "0.1em",
        }
      : { lineHeight: "1.14285714em", marginTop: "0.1em" };
    const createdAt = comment.externalCreatedAt
      ? comment.externalCreatedAt
      : comment.createdAt;
    const updatedAt = comment.externalUpdatedAt
      ? comment.externalUpdatedAt
      : comment.updatedAt;

    const onDraftDeleted = () => {
      onEditClientComment()
        .then(() => {
          if (comment.commentType === "DRAFT") {
            // if we have a standalone draft, removing it should reset task comment icon
            // otherwise there is still a comment-task link
            removeCommentTaskId(comment, task);
          }
        })
        .finally(() => {
          this.setState({ menuOpen: false, loading: false, draftGid: null });
        });
    };

    let unsavedNotice = "";
    if (comment.draft) {
      unsavedNotice = "Unsaved changes";
    } else if (comment.commentType === "DRAFT") {
      unsavedNotice = "Unpublished changes";
    }
    const linkedEntityTitle = task.title || clientWorkflow.name;
    return (
      <Dimmer.Dimmable as={Container} dimmed={loading}>
        <Loading active={loading} messages={[]} inverted />
        <Card
          data-test="client-card-comment"
          fluid
          style={{
            wordWrap: "break-word",
            flex: "inherit",
            ...(unsavedNotice ? { backgroundColor: "lightgray" } : {}),
          }}
          id={task.id}
        >
          <Card.Content>
            <Header>
              <Header.Content
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <div style={{ display: "flex" }} data-test="comment-created-by">
                  {comment.createdBy
                    ? `${comment.createdBy.firstName || ""} ${
                        comment.createdBy.lastName || ""
                      }`
                    : "Imported Comment"}
                </div>
                <div style={{ fontWeight: "bold", display: "inline" }}>
                  <div style={{ float: "right" }}>
                    {!disabled && (
                      <Popup
                        className="hubly_bars_menu"
                        flowing
                        popperModifiers={{
                          preventOverflow: {
                            boundariesElement: "window",
                            enabled: false,
                          },
                        }}
                        position="right center"
                        on="click"
                        onClose={this.toggleMenu}
                        onOpen={this.toggleMenu}
                        open={menuOpen}
                        trigger={
                          <Icon
                            data-test="comment-menu"
                            color="grey"
                            link
                            name="ellipsis horizontal"
                          />
                        }
                      >
                        <Button.Group
                          basic
                          vertical
                          labeled
                          icon
                          style={{ border: "none" }}
                        >
                          <Button
                            icon="edit outline"
                            data-test="edit-comment-menu-item"
                            content="Edit Comment"
                            onClick={this.editComment}
                          />
                          {unsavedNotice ? (
                            <DeleteDraftMenuButton
                              draftGid={
                                comment.draft ? comment.draft.id : comment.id
                              }
                              onDeletionStarted={() => {
                                this.setState({
                                  menuOpen: false,
                                  loading: true,
                                });
                              }}
                              onDraftDeleted={onDraftDeleted}
                              onError={() => {
                                this.setState({ loading: false });
                              }}
                            />
                          ) : (
                            <React.Fragment>
                              <Button
                                icon="eye slash"
                                data-test="hide-comment-menu-item"
                                content="Hide Comment"
                                onClick={this.hideComment}
                              />
                              {advisor.integrations.filter((i) => {
                                return i.hubId === hub.id;
                              }).length === 0 && (
                                <Button
                                  data-test="delete-comment-menu-item"
                                  icon="delete"
                                  content="Delete Comment"
                                  onClick={this.deleteComment}
                                />
                              )}
                            </React.Fragment>
                          )}
                        </Button.Group>
                      </Popup>
                    )}
                  </div>
                </div>
              </Header.Content>
              <Header.Subheader>
                {unsavedNotice && (
                  <div style={{ float: "right" }} data-test="unsaved-changes">
                    <span style={{ fontStyle: "italic" }}>{unsavedNotice}</span>
                  </div>
                )}
              </Header.Subheader>
              <Header.Subheader style={{ paddingTop: "0.25em" }}>
                {`Created on ${Moment(createdAt).format("MMMM D, YYYY")}. `}
                {`Last edited `}
                {comment.lastEditedBy
                  ? `by ${comment.lastEditedBy.firstName || ""} ${
                      comment.lastEditedBy.lastName || ""
                    } ` // The space is necessary
                  : ""}
                {`on ${Moment(updatedAt).format("MMMM D, YYYY")}.`}
              </Header.Subheader>
            </Header>
          </Card.Content>
          <Card.Content>
            <div
              className={piiMask("fs-block dd-privacy-mask")}
              data-test="comment-content"
              style={{ overflowX: "auto" }}
            >
              {parse(
                sanitizeHtml(comment.content, {
                  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
                    "h1",
                    "h2",
                    "img",
                    "u",
                  ]),
                  allowedAttributes: { img: ["alt"], a: ["href"] },
                  // Allow Wealthbox emoji class which we can use to replace emojis img tag
                  allowedClasses: { img: ["js-emoji"] },
                }),
                {
                  replace: (domNode) => {
                    // Parsing Wealthbox emojis to convert to a span with the alt unicode
                    if (
                      domNode.attribs &&
                      domNode.attribs.class === "js-emoji"
                    ) {
                      return React.createElement(
                        "span",
                        {},
                        domNode.attribs.alt
                      );
                    }
                    // Add // to links without http or https at start
                    if (domNode.attribs && domNode.attribs.href) {
                      let url = domNode.attribs.href;
                      if (
                        !domNode.attribs.href.startsWith("http://") &&
                        !domNode.attribs.href.startsWith("https://") &&
                        !domNode.attribs.href.startsWith("//") &&
                        !domNode.attribs.href.startsWith("mailto:")
                      ) {
                        url = `//${domNode.attribs.href}`;
                      }
                      return React.createElement(
                        "a",
                        { href: url, target: "_blank" },
                        domNode.children.length > 0
                          ? domNode.children[0].data
                          : url
                      );
                    }
                    return domNode;
                  },
                }
              )}
            </div>
          </Card.Content>
          {linkedEntityTitle && (
            <Card.Content>
              <div
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", flexWrap: "nowrap" }}>
                  <div>
                    {task.title ? (
                      <Checkbox
                        data-test="comment-task-completed-checkbox"
                        onClick={this.toggleTask}
                        checked={task.completed}
                        style={{
                          marginRight: "1em",
                        }}
                      />
                    ) : (
                      <Icon name="list ul" />
                    )}
                  </div>
                  <div
                    style={textStyle}
                    data-test="comment-linked-entity-title"
                  >
                    <Expandable lines={1}>{linkedEntityTitle}</Expandable>
                  </div>
                </div>
              </div>
            </Card.Content>
          )}
        </Card>
        {openFormModal && (
          <FormModal
            formId={task.formInstanceId}
            onClose={() => {
              this.setState({ openFormModal: false });
            }}
            onDone={() => {
              setTaskComplete(clientWorkflow, task.id, () => {
                this.setState({ openFormModal: false });
              });
            }}
          />
        )}
      </Dimmer.Dimmable>
    );
  };

  render() {
    const { comment } = this.props;
    const { editing } = this.state;
    if (!comment.isHidden && !comment.isCrmOnly) {
      return editing ? this.editingMarkup() : this.displayMarkup();
    }
    return <React.Fragment />;
  }
}
const mapStateToProps = (state) => {
  return {
    advisor: state.hubly.data.advisor,
    hub: state.hubly.data.hub.selected.hub,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    removeCommentTaskId: (comment, task) => {
      dispatch(RemoveCommentTaskId(comment, task));
    },
    onRemoveCommentAssociationId: (comment) => {
      dispatch(publishUpdate("CLIENT_CARD_TASKS", [true]));
    },
    setTaskComplete: (workflow, taskId, onSuccess) => {
      dispatch(SetTaskComplete(workflow, taskId, null, onSuccess));
    },
    setTaskIncomplete: (workflow, taskId) => {
      dispatch(SetTaskIncomplete(workflow, taskId));
    },
    setConfirmationModal: (id) => {
      dispatch(SetConfirmationModal(id));
    },
    setAlert: (alert) => {
      dispatch(SetAlert(alert));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRedtailUrl(CardComment));
